import { useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient, isApiClientError, isAuthRequiredError } from '@/lib/apiClient'
import { useGlobalState } from '@/stores/globalState'
import { readPendingFirstName, clearPendingFirstName } from '@/lib/pendingProfile'
import { readInviteCode, clearInviteCode, markKnownAccount } from '@/lib/inviteCode'
import { isLoggingOut } from '@/lib/logoutLatch'
import type { User } from '@/types'

/**
 * Runs once after Auth0 authentication succeeds.
 * 1. POST /api/users/sync updates the user in our DB. A new Auth0 identity
 *    must present the invite code stashed by /auth/signup, or the backend
 *    answers 403 INVITE_REQUIRED and globalState shows the invite wall.
 * 2. GET /api/users/me fetches the canonical profile.
 * 3. Saves the profile into Zustand globalState.user.
 */
export function useProfileSync() {
  const { isAuthenticated, isLoading, user: auth0User } = useAuth0()
  const user = useGlobalState((s) => s.user)
  const setUser = useGlobalState((s) => s.setUser)
  const setInviteRequired = useGlobalState((s) => s.setInviteRequired)
  const syncing = useRef(false)

  useEffect(() => {
    if (isLoading || !isAuthenticated) return
    // Already have a user in state (e.g. persisted or demo): skip.
    if (user) return
    // During logout, the store user becomes null before Auth0 leaves the page.
    // Do not re-sync and accidentally re-create the session.
    if (isLoggingOut()) return
    // Prevent duplicate calls during StrictMode double-mount.
    if (syncing.current) return
    syncing.current = true

    async function sync() {
      try {
        // Pass the real email and first name from the ID token plus the name
        // stashed at passwordless signup, so the backend can persist them.
        const pendingName = readPendingFirstName()
        const firstName =
          (auth0User?.given_name as string | undefined) ||
          pendingName ||
          (typeof auth0User?.name === 'string' ? auth0User.name.split(' ')[0] : undefined)
        const inviteCode = readInviteCode()
        const syncBody = {
          ...(auth0User?.email ? { email: auth0User.email } : {}),
          ...(firstName ? { firstName } : {}),
          ...(inviteCode ? { inviteCode } : {}),
        }

        await apiClient.post('/api/users/sync', syncBody)
        clearPendingFirstName()
        clearInviteCode()
        markKnownAccount()

        const { data } = await apiClient.get<User>('/api/users/me')
        if (isLoggingOut()) return
        setUser({
          ...data,
          createdAt: new Date(data.createdAt),
        })
        setInviteRequired(false)
      } catch (err) {
        if (isAuthRequiredError(err)) return

        if (isInviteRequiredError(err)) {
          clearInviteCode()
          setInviteRequired(true)
          return
        }
        console.error('[ProfileSync] Failed to sync user profile:', err)
      } finally {
        syncing.current = false
      }
    }

    sync()
  }, [isAuthenticated, isLoading, user, setUser, setInviteRequired, auth0User])
}

function isInviteRequiredError(err: unknown): boolean {
  return (
    isApiClientError(err) &&
    err.status === 403 &&
    typeof err.data === 'object' &&
    err.data !== null &&
    'error' in err.data &&
    err.data.error === 'INVITE_REQUIRED'
  )
}
